import { AICycleContent } from "../../types/ai-templates";
import { motion } from "framer-motion";
import { useState } from "react";
import NetworkIcon from "@core/network-icon";

interface CycleTemplate5Props {
  content: AICycleContent;
  onUpdate: (newContent: AICycleContent) => void;
}

// Animation variants (kept for container fade-in, no SVG animations)
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

export function CycleTemplate5({ content, onUpdate }: CycleTemplate5Props) {
  const [editingStep, setEditingStep] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<
    "title" | "description" | null
  >(null);
  const [tempValue, setTempValue] = useState<string>("");

  // Ensure steps exist
  if (!content.steps || content.steps.length !== 5) {
    return (
      <div className="p-4 text-center text-error">
        Invalid cycle data structure. Exactly 5 steps required.
      </div>
    );
  }

  // Handle step update
  const handleStepUpdate = (
    stepIndex: number,
    field: "title" | "description",
    newText: string,
  ) => {
    const newContent = JSON.parse(JSON.stringify(content));
    if (newContent.steps && newContent.steps[stepIndex]) {
      newContent.steps[stepIndex][field] = newText;
      onUpdate(newContent);
    }
  };

  // Handle title update
  const handleTitleUpdate = (newTitle: string) => {
    const newContent = JSON.parse(JSON.stringify(content));
    newContent.title = newTitle;
    onUpdate(newContent);
  };

  // Handle click to edit
  const startEditing = (
    field: "title" | "description",
    stepIndex: number,
    value: string,
  ) => {
    setEditingField(field);
    setEditingStep(stepIndex);
    setTempValue(value);
  };

  return (
    <motion.div
      className="p-6 bg-base-200 rounded-lg shadow-md overflow-hidden"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <motion.div
        className="relative w-max h-full mx-auto"
        variants={containerVariants}
      >
        <svg
          width="1104"
          height="996"
          viewBox="0 0 1104 996"
          style={{
            fill: "none",
            stroke: "none",
            fillRule: "evenodd",
            clipRule: "evenodd",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeMiterlimit: 1.5,
          }}
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
        >
          <style
            className="text-font-style fontImports"
            data-font-family="Roboto"
          >
            @import
            url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=block');
          </style>

          {/* Patterns */}
          <defs>
            <pattern
              id="crawlingPattern-986aac1a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#986aac1a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-a869901a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#a869901a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-a867651a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#a867651a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-9b7b5f1a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#9b7b5f1a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-958e541a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#958e541a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-0000001b"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#0000001b"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-ddc700ff"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#ddc700ff"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-f17400d3"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#f17400d3"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-f9080081"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#f9080081"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-eb008e76"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#eb008e76"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-a700f578"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#a700f578"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-5f5f5f1a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#5f5f5f1a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <symbol id="crawlingSymbol">
              <g>
                <path d="M -12.5 12.5 l 13 -13 l 6 0 l -13 13 Z M -0.5 12.5 l 13 -13 l 6 0 l -13 13 Z M 11.5 12.5 l 13 -13 l 6 0 l -13 13 Z"></path>
              </g>
            </symbol>
          </defs>

          {/* Step 5 Path */}
          <g
            id="g-root-cp_5_g-5_58cb0vyp0cg-stroke-fill"
            data-item-order="-893510"
            transform="translate(239.38699340820312, 164)"
          >
            <g id="cp_5_g-5_58cb0vyp0cg-fill" stroke="none" fill="#60436d">
              <g>
                <path d="M 300.613 177.663L 300.613 10.2227C 168.604 15.1201 57.1604 100.4842 13.8936 218.704L 173.15 270.449C 184.954 274.285 197.545 268.324 204.297 257.91C 206.42 254.636 208.688 251.464 211.091 248.403C 201.162 238.412 197.635 223.06 203.363 209.233C 210.971 190.864 232.03 182.141 250.399 189.75C 258.645 193.165 264.947 199.291 268.7 206.667C 272.099 205.437 275.565 204.345 279.091 203.399C 291.068 200.185 300.613 190.064 300.613 177.663ZM 250.399 189.75C 232.03 182.141 210.971 190.864 203.363 209.233C 197.635 223.06 201.162 238.412 211.091 248.403C 214.352 251.685 218.304 254.388 222.846 256.269C 241.214 263.878 262.273 255.155 269.882 236.786C 274.075 226.663 273.308 215.723 268.7 206.667C 264.947 199.291 258.645 193.165 250.399 189.75Z"></path>
              </g>
            </g>

            <g
              id="cp_5_g-5_58cb0vyp0cg-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[4].color || "#cb68f9"}
              strokeWidth="2"
            >
              <g>
                <path d="M 300.6131 177.6631L 300.6131 10.2227C 168.604 15.1201 57.1604 100.4842 13.8936 218.7037L 173.1499 270.4492C 184.9541 274.2846 197.5446 268.3239 204.2973 257.9099C 206.4205 254.6356 208.6881 251.4636 211.091 248.403C 201.1618 238.4117 197.6351 223.0601 203.3625 209.2328C 210.9712 190.864 232.03 182.1411 250.3988 189.7498C 258.6445 193.1653 264.9465 199.2911 268.7 206.6673C 272.0994 205.4368 275.5653 204.345 279.0908 203.3989C 291.0677 200.1847 300.6131 190.0638 300.6131 177.6631ZM 250.3988 189.7498C 232.03 182.1411 210.9712 190.864 203.3625 209.2328C 197.6351 223.0601 201.1618 238.4117 211.091 248.403C 214.3522 251.6846 218.3041 254.3879 222.8456 256.2691C 241.2144 263.8777 262.2733 255.1548 269.8819 236.786C 274.075 226.663 273.308 215.7229 268.7 206.6673C 264.9465 199.2911 258.6445 193.1653 250.3988 189.7498ZM 10 230C 51.3159 102.3286 171.1822 10 312.6129 10"></path>
              </g>
            </g>
          </g>

          {/* Step 4 Path */}
          <g
            id="g-root-cp_4_g-4_1qape18vyp0y2-stroke-fill"
            data-item-order="-893506"
            transform="translate(224, 384)"
          >
            <g id="cp_4_g-4_1qape18vyp0y2-fill" stroke="none" fill="#69425a">
              <g>
                <path d="M 199 108.0004C 199 105.8651 199.052 103.742 199.154 101.6322C 199.754 89.2913 193.069 77.1567 181.318 73.3386L 21.8951 21.5391C 18.4717 33.6837 15.7515 46.1231 13.7814 58.8106C 11.2919 74.8426 10 91.2706 10 108.0004C 10 193.673 43.8788 271.432 98.9699 328.612C 109.172 339.201 120.101 349.084 131.678 358.181L 230.097 222.719C 237.387 212.685 235.618 198.882 227.8 189.254C 225.248 186.11 222.84 182.843 220.588 179.465C 211.685 183.734 201.073 184.345 191.233 180.269C 172.864 172.66 164.141 151.602 171.75 133.233C 176.674 121.345 187.231 113.498 199.047 111.502C 199.016 110.338 199 109.1712 199 108.0004ZM 238.269 160.786C 245.878 142.417 237.155 121.358 218.786 113.75C 212.305 111.065 205.489 110.414 199.047 111.502C 187.231 113.498 176.674 121.345 171.75 133.233C 164.141 151.602 172.864 172.66 191.233 180.269C 201.073 184.345 211.685 183.734 220.588 179.465C 228.304 175.764 234.736 169.315 238.269 160.786Z"></path>
              </g>
            </g>

            <g
              id="cp_4_g-4_1qape18vyp0y2-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[3].color || "#f366bb"}
              strokeWidth="2"
            >
              <g>
                <path d="M 199 108.0004C 199 105.8651 199.0519 103.742 199.1544 101.6322C 199.7544 89.2913 193.0686 77.1567 181.3179 73.3386L 21.8951 21.5391C 18.4717 33.6837 15.7515 46.1231 13.7814 58.8106C 11.2919 74.8426 10 91.2706 10 108.0004C 10 193.6725 43.8788 271.4318 98.9699 328.6118C 109.172 339.2007 120.1014 349.0839 131.678 358.1809L 230.097 222.7188C 237.3869 212.6851 235.6179 198.8823 227.8005 189.2539C 225.2478 186.1099 222.8402 182.8434 220.5877 179.4646C 211.6849 183.7342 201.0729 184.345 191.2328 180.2691C 172.864 172.6605 164.1411 151.6016 171.7498 133.2328C 176.6737 121.3453 187.2309 113.4976 199.0466 111.5018C 199.0156 110.3385 199 109.1712 199 108.0004ZM 238.2691 160.786C 245.8777 142.4172 237.1548 121.3584 218.786 113.7498C 212.3048 111.0651 205.4886 110.4137 199.0466 111.5018C 187.2309 113.4976 176.6737 121.3453 171.7498 133.2328C 164.1411 151.6016 172.864 172.6605 191.2328 180.2691C 201.0729 184.345 211.6849 183.7342 220.5877 179.4646C 228.304 175.764 234.7364 169.3148 238.2691 160.786ZM 141.2564 365.4203C 126.0948 354.4024 111.944 342.0779 98.9699 328.6118M 25.3871 10C 20.2847 25.7669 16.3804 42.0729 13.7814 58.8106"></path>
              </g>
            </g>
          </g>

          {/* Step 3 Path */}
          <g
            id="g-root-cp_3_g-3_1lviczwvyoykk-stroke-fill"
            data-item-order="-893502"
            transform="translate(355.2559814453125, 575)"
          >
            <g id="cp_3_g-3_1lviczwvyoykk-fill" stroke="none" fill="#68403f">
              <g>
                <path d="M 373.653 181.286L 275.193 45.7673C 267.914 35.7492 254.27 33.1614 242.7 37.5739C 239.335 38.8572 235.903 40.0037 232.41 41.0066C 233.279 47.1631 232.564 53.6258 230.013 59.786C 222.404 78.1548 201.345 86.8777 182.976 79.2691C 167.488 72.8537 158.858 56.8759 161.093 41.011C 157.596 40.0074 154.159 38.8598 150.79 37.5752C 139.22 33.1631 125.577 35.751 118.298 45.7688L 19.8375 181.288C 70.4151 215.21 131.268 234.998 196.744 234.998C 262.22 234.998 323.075 215.209 373.653 181.286ZM 210.53 12.7498C 192.161 5.1411 171.102 13.864 163.493 32.2328C 162.3 35.1135 161.509 38.0603 161.093 41.011C 158.858 56.8759 167.488 72.8537 182.976 79.2691C 201.345 86.8777 222.404 78.1548 230.013 59.786C 232.564 53.6258 233.279 47.1631 232.41 41.0066C 230.686 28.8053 222.738 17.8067 210.53 12.7498Z"></path>
              </g>
            </g>

            <g
              id="cp_3_g-3_1lviczwvyoykk-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[2].color || "#fb6762"}
              strokeWidth="2"
            >
              <g>
                <path d="M 373.6531 181.2861L 275.1929 45.7673C 267.9144 35.7492 254.2704 33.1614 242.7003 37.5739C 239.3353 38.8572 235.9028 40.0037 232.4095 41.0066C 233.2792 47.1631 232.5644 53.6258 230.0127 59.786C 222.4041 78.1548 201.3453 86.8777 182.9765 79.2691C 167.4883 72.8537 158.8579 56.8759 161.0929 41.011C 157.5955 40.0074 154.1589 38.8598 150.7901 37.5752C 139.2201 33.1631 125.5766 35.751 118.2983 45.7688L 19.8375 181.2884C 70.4151 215.2103 131.2684 234.9982 196.7436 234.9982C 262.2202 234.9982 323.0748 215.2094 373.6531 181.2861ZM 210.5297 12.7498C 192.1609 5.1412 171.102 13.864 163.4934 32.2328C 162.3002 35.1135 161.5086 38.0603 161.0929 41.011C 158.8579 56.8759 167.4883 72.8537 182.9765 79.2691C 201.3453 86.8777 222.4041 78.1548 230.0127 59.786C 232.5644 53.6258 233.2792 47.1631 232.4095 41.0066C 230.6859 28.8053 222.7383 17.8067 210.5297 12.7498ZM 383.4934 174.4141C 380.2588 176.7648 376.9781 179.056 373.6531 181.2861L 373.6541 181.2876C 323.0757 215.2113 262.2207 235.0004 196.7436 235.0004C 131.268 235.0004 70.4142 215.2122 19.8364 181.2899L 19.8375 181.2884C 16.5134 179.059 13.2337 176.7685 10 174.4186"></path>
              </g>
            </g>
          </g>

          {/* Step 2 Path */}
          <g
            id="g-root-cp_2_g-2_1lviczwvyoykm-stroke-fill"
            data-item-order="-893498"
            transform="translate(629, 384)"
          >
            <g id="cp_2_g-2_1lviczwvyoykm-fill" stroke="none" fill="#5c4938">
              <g>
                <path d="M 20.905885 222.716L 119.324985 358.179C 160.565985 325.771 193.594985 283.385 214.775985 234.659C 231.645985 195.85 240.999985 153.018 240.999985 108.0008C 240.999985 87.3957 239.039985 67.2483 235.296985 47.7349C 233.595985 38.8651 231.525985 30.1264 229.103985 21.5352L 69.682085 73.3344C 57.931085 77.1526 51.245285 89.2877 51.845585 101.6289C 51.948185 103.7399 52.000185 105.8643 52.000185 108.0008C 52.000185 109.1694 51.984585 110.334 51.953785 111.495C 54.587685 111.939 57.213285 112.684 59.785985 113.75C 78.154785 121.358 86.877685 142.417 79.269085 160.786C 71.660485 179.155 50.601585 187.878 32.232785 180.269C 31.616585 180.014 31.011185 179.743 30.416885 179.458C 28.163785 182.839 25.755385 186.106 23.201885 189.252C 15.384885 198.88 13.616185 212.682 20.905885 222.716ZM 79.269085 160.786C 86.877685 142.417 78.154785 121.358 59.785985 113.75C 57.213285 112.684 54.587685 111.939 51.953785 111.495C 35.781985 108.7738 19.292685 117.437 12.749785 133.233C 5.396385 150.985 13.297085 171.25 30.416885 179.458C 31.011185 179.743 31.616585 180.014 32.232785 180.269C 50.601585 187.878 71.660485 179.155 79.269085 160.786Z"></path>
              </g>
            </g>

            <g
              id="cp_2_g-2_1lviczwvyoykm-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[1].color || "#f79438"}
              strokeWidth="2"
            >
              <g>
                <path d="M 20.905885 222.7158L 119.325385 358.1788C 160.565785 325.7708 193.594685 283.3852 214.775685 234.6595C 231.646085 195.8498 241.000185 153.0176 241.000185 108.0008C 241.000185 87.3957 239.040385 67.2483 235.297185 47.7349C 233.595685 38.8651 231.525685 30.1264 229.103785 21.5352L 69.682085 73.3344C 57.931085 77.1526 51.245285 89.2877 51.845585 101.6289C 51.948185 103.7399 52.000185 105.8643 52.000185 108.0008C 52.000185 109.1694 51.984585 110.3343 51.953785 111.4955C 54.587685 111.9388 57.213285 112.6841 59.785985 113.7498C 78.154785 121.3584 86.877685 142.4172 79.269085 160.786C 71.660485 179.1548 50.601585 187.8777 32.232785 180.2691C 31.616585 180.0138 31.011185 179.7434 30.416885 179.4585C 28.163785 182.8386 25.755385 186.1064 23.201885 189.2516C 15.384885 198.88 13.616185 212.6824 20.905885 222.7158ZM 79.269085 160.786C 86.877685 142.4172 78.154785 121.3584 59.785985 113.7498C 57.213285 112.6841 54.587685 111.9388 51.953785 111.4955C 35.781985 108.7738 19.292685 117.4368 12.749785 133.2328C 5.396385 150.9854 13.297085 171.2505 30.416885 179.4585C 31.011185 179.7434 31.616585 180.0138 32.232785 180.2691C 50.601585 187.8777 71.660485 179.1548 79.269085 160.786ZM 225.613085 10C 229.583385 22.2687 232.828185 34.8639 235.297185 47.7349M 109.749985 365.4158C 155.459785 332.197 191.979685 287.1004 214.775685 234.6595"></path>
              </g>
            </g>
          </g>

          {/* Step 1 Path */}
          <g
            id="g-root-cp_1_g-1_1hfouvgvyozcw-stroke-fill"
            data-item-order="-893494"
            transform="translate(542, 164)"
          >
            <g id="cp_1_g-1_1hfouvgvyozcw-fill" stroke="none" fill="#54502f">
              <g>
                <path d="M 149.46 270.445L 308.718 218.7C 265.45 100.4823 154.007 15.1201 22 10.2227L 22 177.663C 22 190.064 31.5453 200.185 43.5222 203.399C 47.0528 204.346 50.5235 205.44 53.9276 206.673C 62.3597 190.07 82.2927 182.504 99.786 189.75C 118.155 197.358 126.878 218.417 119.269 236.786C 117.412 241.269 114.754 245.177 111.53 248.414C 113.929 251.47 116.193 254.637 118.313 257.907C 125.066 268.32 137.656 274.281 149.46 270.445ZM 119.269 236.786C 126.878 218.417 118.155 197.358 99.786 189.75C 82.2927 182.504 62.3597 190.07 53.9276 206.673C 53.5056 207.504 53.1124 208.357 52.7498 209.233C 45.1412 227.602 53.864 248.66 72.2328 256.269C 86.1189 262.021 101.5422 258.44 111.53 248.414C 114.754 245.177 117.412 241.269 119.269 236.786Z"></path>
              </g>
            </g>

            <g
              id="cp_1_g-1_1hfouvgvyozcw-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[0].color || "#ffe711"}
              strokeWidth="2"
            >
              <g>
                <path d="M 149.4603 270.4454L 308.7177 218.6995C 265.4498 100.4823 154.0074 15.1201 22 10.2227L 22 177.6632C 22 190.0639 31.5453 200.1847 43.5222 203.3989C 47.0528 204.3464 50.5235 205.44 53.9276 206.6727C 62.3597 190.0696 82.2927 182.5038 99.786 189.7498C 118.1548 197.3584 126.8777 218.4172 119.2691 236.786C 117.4123 241.2688 114.7544 245.1771 111.53 248.4137C 113.9292 251.47 116.1933 254.6374 118.3134 257.9068C 125.0662 268.3203 137.6564 274.2807 149.4603 270.4454ZM 119.2691 236.786C 126.8777 218.4172 118.1548 197.3584 99.786 189.7498C 82.2927 182.5038 62.3597 190.0696 53.9276 206.6727C 53.5056 207.5037 53.1124 208.3573 52.7498 209.2328C 45.1412 227.6016 53.864 248.6605 72.2328 256.2691C 86.1189 262.0209 101.5422 258.4397 111.53 248.4137C 114.7544 245.1771 117.4123 241.2688 119.2691 236.786ZM 10 10C 151.4307 10 271.297 102.3286 312.6129 230"></path>
              </g>
            </g>
          </g>

          {/* Main Title */}
          <g
            id="g-root-tx_cycleofh_18k1umkvyp05m-stroke-fill"
            data-item-order="0"
            transform="translate(332, 50)"
          >
            <g id="tx_cycleofh_18k1umkvyp05m-fill" stroke="none" fill="#171717">
              <foreignObject x="17.63" y="20" width="400" height="30">
                <div
                  className="text-center"
                  style={{
                    font: "bold 25px Roboto, sans-serif",
                    color: "#171717",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    display:
                      editingField === "title" && editingStep === -1
                        ? "none"
                        : "block",
                  }}
                  onClick={() => startEditing("title", -1, content.title)}
                >
                  {content.title}
                </div>
                {editingField === "title" && editingStep === -1 && (
                  <input
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.title) {
                        handleTitleUpdate(tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.currentTarget.blur();
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{ font: "bold 25px Roboto, sans-serif" }}
                  />
                )}
              </foreignObject>
            </g>
          </g>

          {/* Number Labels */}
          <g
            id="g-root-tx_1_1448ci4vyp0xv-stroke-fill"
            data-item-order="0"
            transform="translate(605, 358)"
          >
            <g
              id="tx_1_1448ci4vyp0xv-fill"
              stroke="none"
              fill={content.steps[0].color || "#ffe711"}
            >
              <text
                style={{
                  font: "bold 30px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="30px"
                fontFamily="Roboto, sans-serif"
              >
                <tspan x="13.39" y="45.5" dominant-baseline="ideographic">
                  1
                </tspan>
              </text>
            </g>
          </g>
          <g
            id="g-root-tx_2_v97tccvyoyks-stroke-fill"
            data-item-order="0"
            transform="translate(653, 502)"
          >
            <g
              id="tx_2_v97tccvyoyks-fill"
              stroke="none"
              fill={content.steps[1].color || "#f79438"}
            >
              <text
                style={{
                  font: "bold 30px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="30px"
                fontFamily="Roboto, sans-serif"
              >
                <tspan x="13.39" y="45.5" dominant-baseline="ideographic">
                  2
                </tspan>
              </text>
            </g>
          </g>
          <g
            id="g-root-tx_3_qteb7wvyozd1-stroke-fill"
            data-item-order="0"
            transform="translate(530, 590)"
          >
            <g
              id="tx_3_qteb7wvyozd1-fill"
              stroke="none"
              fill={content.steps[2].color || "#fb6762"}
            >
              <text
                style={{
                  font: "bold 30px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="30px"
                fontFamily="Roboto, sans-serif"
              >
                <tspan x="13.39" y="45.5" dominant-baseline="ideographic">
                  3
                </tspan>
              </text>
            </g>
          </g>
          <g
            id="g-root-tx_4_qteb7wvyozd2-stroke-fill"
            data-item-order="0"
            transform="translate(407, 500)"
          >
            <g
              id="tx_4_qteb7wvyozd2-fill"
              stroke="none"
              fill={content.steps[3].color || "#f366bb"}
            >
              <text
                style={{
                  font: "bold 30px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="30px"
                fontFamily="Roboto, sans-serif"
              >
                <tspan x="13.39" y="45.5" dominant-baseline="ideographic">
                  4
                </tspan>
              </text>
            </g>
          </g>
          <g
            id="g-root-tx_5_hxraz0vyp05r-stroke-fill"
            data-item-order="0"
            transform="translate(453, 358)"
          >
            <g
              id="tx_5_hxraz0vyp05r-fill"
              stroke="none"
              fill={content.steps[4].color || "#cb68f9"}
            >
              <text
                style={{
                  font: "bold 30px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="30px"
                fontFamily="Roboto, sans-serif"
              >
                <tspan x="13.39" y="45.5" dominant-baseline="ideographic">
                  5
                </tspan>
              </text>
            </g>
          </g>

          {/* Icons */}
          <g
            id="g-root-ic_aler_mdkt3gvyp05c-stroke-fill"
            data-item-order="0"
            transform="translate(658, 269)"
          >
            <foreignObject x="0" y="0" width="60" height="60">
              <NetworkIcon
                value={content.steps[0].iconId || "alert-circle"}
                type="LUCIDE"
                style={{
                  color: content.steps[0].color || "#ffe711",
                  width: "60px",
                  height: "60px",
                }}
              />
            </foreignObject>
          </g>
          <g
            id="g-root-ic_buil_zknzv0vz3218-stroke-fill"
            data-item-order="0"
            transform="translate(740, 536)"
          >
            <foreignObject x="0" y="0" width="60" height="60">
              <NetworkIcon
                value={content.steps[1].iconId || "building-2"}
                type="LUCIDE"
                style={{
                  color: content.steps[1].color || "#f79438",
                  width: "60px",
                  height: "60px",
                }}
              />
            </foreignObject>
          </g>
          <g
            id="g-root-ic_wren_143lvf0vyt895-stroke-fill"
            data-item-order="0"
            transform="translate(518, 686)"
          >
            <foreignObject x="0" y="0" width="60" height="60">
              <NetworkIcon
                value={content.steps[2].iconId || "wrench"}
                type="LUCIDE"
                style={{
                  color: content.steps[2].color || "#fb6762",
                  width: "60px",
                  height: "60px",
                }}
              />
            </foreignObject>
          </g>
          <g
            id="g-root-ic_chec_1qeg8jwvz06ir-stroke-fill"
            data-item-order="0"
            transform="translate(380, 269)"
          >
            <foreignObject x="0" y="0" width="60" height="60">
              <NetworkIcon
                value={content.steps[3].iconId || "check-square"}
                type="LUCIDE"
                style={{
                  color: content.steps[3].color || "#f366bb",
                  width: "60px",
                  height: "60px",
                }}
              />
            </foreignObject>
          </g>
          <g
            id="g-root-ic_3_mcbux8vyw1du-stroke-fill"
            data-item-order="0"
            transform="translate(298, 530)"
          >
            <foreignObject x="0" y="0" width="60" height="60">
              <NetworkIcon
                value={content.steps[4].iconId || "compass"}
                type="LUCIDE"
                style={{
                  color: content.steps[4].color || "#cb68f9",
                  width: "60px",
                  height: "60px",
                }}
              />
            </foreignObject>
          </g>

          {/* Step 1 Title and Description */}
          <g
            id="g-root-tx_identify_v7yv64vz4glp-stroke-fill"
            data-item-order="0"
            transform="translate(800, 110)"
          >
            <g
              id="tx_identify_v7yv64vz4glp-fill"
              stroke="none"
              fill={content.steps[0].color || "#ffe711"}
            >
              <foreignObject x="12" y="10" width="200" height="80">
                <div
                  style={{
                    font: "bold 20px Roboto, sans-serif",
                    color: content.steps[0].color || "#ffe711",
                    whiteSpace: "pre-wrap",
                    cursor: "pointer",
                    display:
                      editingField === "title" && editingStep === 0
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("title", 0, content.steps[0].title)
                  }
                >
                  {content.steps[0].title}
                </div>
                {editingField === "title" && editingStep === 0 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[0].title) {
                        handleStepUpdate(0, "title", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{ font: "bold 20px Roboto, sans-serif" }}
                  />
                )}
              </foreignObject>
            </g>
          </g>
          <g
            id="g-root-tx_recogniz_zqwqq4vz4gzq-stroke-fill"
            data-item-order="0"
            transform="translate(800, 140)"
          >
            <g id="tx_recogniz_zqwqq4vz4gzq-fill" stroke="none" fill="#171717">
              <foreignObject x="12" y="10" width="200" height="60">
                <div
                  style={{
                    font: "15px Roboto, sans-serif",
                    color: "#171717",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    maxWidth: "200px",
                    cursor: "pointer",
                    display:
                      editingField === "description" && editingStep === 0
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("description", 0, content.steps[0].description)
                  }
                >
                  {content.steps[0].description}
                </div>
                {editingField === "description" && editingStep === 0 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[0].description) {
                        handleStepUpdate(0, "description", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{
                      font: "15px Roboto, sans-serif",
                      maxWidth: "200px",
                    }}
                  />
                )}
              </foreignObject>
            </g>
          </g>

          {/* Step 2 Title and Description */}
          <g
            id="g-root-tx_analyzes_qvw7kcvz4emn-stroke-fill"
            data-item-order="0"
            transform="translate(884, 536)"
          >
            <g
              id="tx_analyzes_qvw7kcvz4emn-fill"
              stroke="none"
              fill={content.steps[1].color || "#f79438"}
            >
              <foreignObject x="12" y="10" width="200" height="80">
                <div
                  style={{
                    font: "bold 20px Roboto, sans-serif",
                    color: content.steps[1].color || "#f79438",
                    whiteSpace: "pre-wrap",
                    cursor: "pointer",
                    display:
                      editingField === "title" && editingStep === 1
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("title", 1, content.steps[1].title)
                  }
                >
                  {content.steps[1].title}
                </div>
                {editingField === "title" && editingStep === 1 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[1].title) {
                        handleStepUpdate(1, "title", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{ font: "bold 20px Roboto, sans-serif" }}
                  />
                )}
              </foreignObject>
            </g>
          </g>
          <g
            id="g-root-tx_understa_mg2pfwvz4few-stroke-fill"
            data-item-order="0"
            transform="translate(884, 570)"
          >
            <g id="tx_understa_mg2pfwvz4few-fill" stroke="none" fill="#171717">
              <foreignObject x="12" y="10" width="200" height="80">
                <div
                  style={{
                    font: "15px Roboto, sans-serif",
                    color: "#171717",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    maxWidth: "200px",
                    cursor: "pointer",
                    display:
                      editingField === "description" && editingStep === 1
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("description", 1, content.steps[1].description)
                  }
                >
                  {content.steps[1].description}
                </div>
                {editingField === "description" && editingStep === 1 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[1].description) {
                        handleStepUpdate(1, "description", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{
                      font: "15px Roboto, sans-serif",
                      maxWidth: "200px",
                    }}
                  />
                )}
              </foreignObject>
            </g>
          </g>

          {/* Step 3 Title and Description */}
          <g
            id="g-root-tx_develops_1qf2pn0vz4g05-stroke-fill"
            data-item-order="0"
            transform="translate(460, 830)"
          >
            <g
              id="tx_develops_1qf2pn0vz4g05-fill"
              stroke="none"
              fill={content.steps[2].color || "#fb6762"}
            >
              <foreignObject x="13.94" y="10" width="200" height="80">
                <div
                  style={{
                    font: "bold 20px Roboto, sans-serif",
                    color: content.steps[2].color || "#fb6762",
                    whiteSpace: "pre-wrap",
                    cursor: "pointer",
                    display:
                      editingField === "title" && editingStep === 2
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("title", 2, content.steps[2].title)
                  }
                >
                  {content.steps[2].title}
                </div>
                {editingField === "title" && editingStep === 2 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[2].title) {
                        handleStepUpdate(2, "title", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{ font: "bold 20px Roboto, sans-serif" }}
                  />
                )}
              </foreignObject>
            </g>
          </g>
          <g
            id="g-root-tx_createal_1hjfpe4vz4gsu-stroke-fill"
            data-item-order="0"
            transform="translate(460, 870)"
          >
            <g id="tx_createal_1hjfpe4vz4gsu-fill" stroke="none" fill="#171717">
              <foreignObject x="15.03" y="10" width="200" height="60">
                <div
                  style={{
                    font: "15px Roboto, sans-serif",
                    color: "#171717",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    maxWidth: "200px",
                    cursor: "pointer",
                    display:
                      editingField === "description" && editingStep === 2
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("description", 2, content.steps[2].description)
                  }
                >
                  {content.steps[2].description}
                </div>
                {editingField === "description" && editingStep === 2 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[2].description) {
                        handleStepUpdate(2, "description", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{
                      font: "15px Roboto, sans-serif",
                      maxWidth: "200px",
                    }}
                  />
                )}
              </foreignObject>
            </g>
          </g>

          {/* Step 4 Title and Description */}
          <g
            id="g-root-tx_integrat_4pf618vz4emb-stroke-fill"
            data-item-order="0"
            transform="translate(30, 548)"
          >
            <g
              id="tx_integrat_4pf618vz4emb-fill"
              stroke="none"
              fill={content.steps[3].color || "#f366bb"}
            >
              <foreignObject x="22.22" y="10" width="200" height="80">
                <div
                  style={{
                    font: "bold 20px Roboto, sans-serif",
                    color: content.steps[3].color || "#f366bb",
                    whiteSpace: "pre-wrap",
                    cursor: "pointer",
                    display:
                      editingField === "title" && editingStep === 3
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("title", 3, content.steps[3].title)
                  }
                >
                  {content.steps[3].title}
                </div>
                {editingField === "title" && editingStep === 3 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[3].title) {
                        handleStepUpdate(3, "title", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{ font: "bold 20px Roboto, sans-serif" }}
                  />
                )}
              </foreignObject>
            </g>
          </g>
          <g
            id="g-root-tx_applythe_1uuw7rgvz4f7w-stroke-fill"
            data-item-order="0"
            transform="translate(30, 580)"
          >
            <g id="tx_applythe_1uuw7rgvz4f7w-fill" stroke="none" fill="#171717">
              <foreignObject x="20.45" y="10" width="200" height="60">
                <div
                  style={{
                    font: "15px Roboto, sans-serif",
                    color: "#171717",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    maxWidth: "200px",
                    cursor: "pointer",
                    display:
                      editingField === "description" && editingStep === 3
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("description", 3, content.steps[3].description)
                  }
                >
                  {content.steps[3].description}
                </div>
                {editingField === "description" && editingStep === 3 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[3].description) {
                        handleStepUpdate(3, "description", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{
                      font: "15px Roboto, sans-serif",
                      maxWidth: "200px",
                    }}
                  />
                )}
              </foreignObject>
            </g>
          </g>

          {/* Step 5 Title and Description */}
          <g
            id="g-root-tx_evaluate_i097bgvz4g75-stroke-fill"
            data-item-order="0"
            transform="translate(130, 110)"
          >
            <g
              id="tx_evaluate_i097bgvz4g75-fill"
              stroke="none"
              fill={content.steps[4].color || "#cb68f9"}
            >
              <foreignObject x="15.54" y="10" width="200" height="80">
                <div
                  style={{
                    font: "bold 20px Roboto, sans-serif",
                    color: content.steps[4].color || "#cb68f9",
                    whiteSpace: "pre-wrap",
                    cursor: "pointer",
                    display:
                      editingField === "title" && editingStep === 4
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("title", 4, content.steps[4].title)
                  }
                >
                  {content.steps[4].title}
                </div>
                {editingField === "title" && editingStep === 4 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[4].title) {
                        handleStepUpdate(4, "title", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{ font: "bold 20px Roboto, sans-serif" }}
                  />
                )}
              </foreignObject>
            </g>
          </g>
          <g
            id="g-root-tx_assessth_94m72kvz4gzu-stroke-fill"
            data-item-order="0"
            transform="translate(130, 140)"
          >
            <g id="tx_assessth_94m72kvz4gzu-fill" stroke="none" fill="#171717">
              <foreignObject x="16.59" y="10" width="200" height="80">
                <div
                  style={{
                    font: "15px Roboto, sans-serif",
                    color: "#171717",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    maxWidth: "200px",
                    cursor: "pointer",
                    display:
                      editingField === "description" && editingStep === 4
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("description", 4, content.steps[4].description)
                  }
                >
                  {content.steps[4].description}
                </div>
                {editingField === "description" && editingStep === 4 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[4].description) {
                        handleStepUpdate(4, "description", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{
                      font: "15px Roboto, sans-serif",
                      maxWidth: "200px",
                    }}
                  />
                )}
              </foreignObject>
            </g>
          </g>
        </svg>
      </motion.div>
    </motion.div>
  );
}
